import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCustomerExperienceAreas,
  fetchCustomerExperienceOverview,
  fetchCustomerExperienceSummary,
  fetchCustomerProactiveOutreach,
  fetchHighRiskCustomers,
} from '../lib/customerExperienceApi';
import { customerExperienceQueryKeys } from '../lib/customerExperienceQueryKeys';
import { filterExperienceItems, sortByExperienceScoreDesc } from '../lib/customerExperienceFormatters';

export const useCustomerExperienceData = () => {
  const [filters, setFilters] = useState({
    area: '',
    experienceLevel: '',
    complaintRisk: '',
    churnRisk: '',
    outreachPriority: '',
  });

  const summaryQuery = useQuery({
    queryKey: customerExperienceQueryKeys.summary,
    queryFn: fetchCustomerExperienceSummary,
    refetchInterval: 20000,
  });

  const overviewQuery = useQuery({
    queryKey: customerExperienceQueryKeys.overview,
    queryFn: fetchCustomerExperienceOverview,
    refetchInterval: 20000,
  });

  const areasQuery = useQuery({
    queryKey: customerExperienceQueryKeys.areas('all'),
    queryFn: () => fetchCustomerExperienceAreas(null),
    refetchInterval: 30000,
  });

  const outreachQuery = useQuery({
    queryKey: customerExperienceQueryKeys.proactiveOutreach,
    queryFn: fetchCustomerProactiveOutreach,
    refetchInterval: 20000,
  });

  const highRiskQuery = useQuery({
    queryKey: customerExperienceQueryKeys.highRisk,
    queryFn: fetchHighRiskCustomers,
    refetchInterval: 20000,
  });

  const records = useMemo(() => sortByExperienceScoreDesc(overviewQuery.data?.records || []), [overviewQuery.data?.records]);
  const filteredRecords = useMemo(() => filterExperienceItems(records, filters), [records, filters]);
  const trends = useMemo(() => overviewQuery.data?.trends || [], [overviewQuery.data?.trends]);

  const isLoading = summaryQuery.isLoading || overviewQuery.isLoading || areasQuery.isLoading || outreachQuery.isLoading || highRiskQuery.isLoading;
  const error = summaryQuery.error || overviewQuery.error || areasQuery.error || outreachQuery.error || highRiskQuery.error;

  const refreshAll = () => {
    summaryQuery.refetch();
    overviewQuery.refetch();
    areasQuery.refetch();
    outreachQuery.refetch();
    highRiskQuery.refetch();
  };

  return {
    summary: summaryQuery.data || {},
    records,
    filteredRecords,
    trends,
    areas: areasQuery.data?.items || [],
    outreachCandidates: outreachQuery.data?.items || [],
    highRiskCustomers: highRiskQuery.data?.items || [],
    filters,
    setFilters,
    refreshAll,
    isLoading,
    error,
  };
};

export default useCustomerExperienceData;
